'use client';

/**
 * Drains the offline write queue on reconnect (audit finding OFF-01).
 *
 * This hook is the *only* consumer of the Dexie `sync_queue` — and until this
 * change it was never mounted anywhere, so the queue was never drained at all.
 * Combined with a `processSyncItem` that handled only a `save_comment` stub
 * (which returned `true` without making any server call, marking items "synced"
 * that were never sent) and marked every real action `failed` with no retry,
 * the net effect was that offline writes were optimistically shown, promised by
 * the offline banner, and then silently discarded.
 *
 * It is now mounted in `QueryProvider`, so it runs for the whole authenticated
 * app. Behaviour:
 *
 *   - Drains oldest-first, so a create always replays before the update that
 *     followed it.
 *   - Stops the moment connectivity drops again, leaving the rest pending.
 *   - Retries transient failures with exponential backoff, up to MAX_ATTEMPTS.
 *   - Abandons permanently-impossible items immediately (validation rejected,
 *     row deleted, no handler) rather than burning retries.
 *   - Surfaces anything it abandons through `onPermanentFailure`, so a write
 *     can never vanish without the user being told.
 */

import { useEffect, useRef, useCallback, useState } from 'react';
import { useOnlineStatus } from './use-online-status';
import {
  getSyncQueueItems,
  getFailedSyncQueueItems,
  markSynced,
  markAttemptFailed,
  markFailed,
  clearSyncedItems,
  requeueFailedItem,
  discardFailedItem,
  type SyncQueueItem,
} from '@/lib/db/offline-db';
import {
  replaySyncAction,
  PermanentSyncError,
  describeSyncAction,
} from '@/lib/db/sync-handlers';
import { clientLogger } from '@/lib/client-logger';

/** Attempts before an item is abandoned and reported to the user. */
export const MAX_SYNC_ATTEMPTS = 5;

/** 2s, 4s, 8s, 16s, 32s — capped so a long outage doesn't push it to hours. */
export function syncBackoffMs(attempts: number): number {
  return Math.min(2_000 * 2 ** (attempts - 1), 60_000);
}

export interface BackgroundSyncState {
  /** Items abandoned after exhausting retries (or rejected outright). */
  failedItems: SyncQueueItem[];
  /** True while a drain pass is in flight. */
  isSyncing: boolean;
  /** Put an abandoned item back in the queue and drain immediately. */
  retryItem: (id: number) => Promise<void>;
  /** Drop an abandoned item for good. */
  discardItem: (id: number) => Promise<void>;
  /** Force a drain pass (e.g. from a "retry all" button). */
  syncNow: () => Promise<void>;
}

export function useBackgroundSync(): BackgroundSyncState {
  const { isOnline } = useOnlineStatus();
  const isSyncingRef = useRef(false);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [failedItems, setFailedItems] = useState<SyncQueueItem[]>([]);
  const [isSyncing, setIsSyncing] = useState(false);

  // Read `isOnline` from a ref inside the loop so a mid-drain disconnect stops
  // it, without making `processQueue` a new function on every status flip.
  //
  // The ref is written from an effect, not during render: a render can be
  // thrown away or replayed (StrictMode, Suspense, concurrent rendering), and
  // a ref written on a discarded render leaks that value into the committed
  // tree. `useRef(isOnline)` seeds the correct value for the first pass, and
  // the effect commits every change after it.
  const isOnlineRef = useRef(isOnline);
  useEffect(() => {
    isOnlineRef.current = isOnline;
  }, [isOnline]);

  const refreshFailed = useCallback(async () => {
    try {
      setFailedItems(await getFailedSyncQueueItems());
    } catch (error) {
      clientLogger.error('[background-sync] failed to read failed items', {
        err: error,
      });
    }
  }, []);

  const processQueue = useCallback(async () => {
    if (isSyncingRef.current) return;
    isSyncingRef.current = true;
    setIsSyncing(true);

    try {
      const items = await getSyncQueueItems();
      if (items.length === 0) return;

      clientLogger.info('[background-sync] draining queue', {
        count: items.length,
      });

      for (const item of items) {
        if (item.id === undefined) continue;
        // Connectivity dropped again mid-drain — leave the rest pending rather
        // than burning attempts on requests that are certain to fail.
        if (!isOnlineRef.current) break;

        try {
          await replaySyncAction(item.action, item.payload);
          await markSynced(item.id);
        } catch (error) {
          const message =
            error instanceof Error ? error.message : String(error);

          if (error instanceof PermanentSyncError) {
            clientLogger.error('[background-sync] abandoning unsyncable item', {
              itemId: item.id,
              action: item.action,
              err: message,
            });
            await markFailed(item.id, message);
            continue;
          }

          const { attempts, exhausted } = await markAttemptFailed(
            item.id,
            message,
            { maxAttempts: MAX_SYNC_ATTEMPTS, backoffMs: syncBackoffMs },
          );

          clientLogger.warn('[background-sync] attempt failed', {
            itemId: item.id,
            action: item.action,
            attempts,
            exhausted,
            err: message,
          });
        }
      }

      await clearSyncedItems();
    } catch (error) {
      clientLogger.error('[background-sync] drain pass threw', { err: error });
    } finally {
      isSyncingRef.current = false;
      setIsSyncing(false);
      await refreshFailed();
    }
  }, [refreshFailed]);

  // Drain on mount and whenever connectivity returns.
  useEffect(() => {
    if (isOnline) void processQueue();
  }, [isOnline, processQueue]);

  // Re-arm a timer while items are waiting out their backoff, so a transient
  // failure resolves on its own instead of waiting for the next online event.
  useEffect(() => {
    if (!isOnline) return;

    let cancelled = false;

    const scheduleNext = async () => {
      const { getDeferredSyncQueueItems } = await import('@/lib/db/offline-db');
      const deferred = await getDeferredSyncQueueItems();
      if (cancelled || deferred.length === 0) return;

      const soonest = Math.min(
        ...deferred.map((i) => i.next_attempt_at ?? Number.MAX_SAFE_INTEGER),
      );
      const delay = Math.max(soonest - Date.now(), 500);

      retryTimerRef.current = setTimeout(() => {
        void processQueue().then(scheduleNext);
      }, delay);
    };

    void scheduleNext();

    return () => {
      cancelled = true;
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
    };
  }, [isOnline, processQueue, isSyncing]);

  const retryItem = useCallback(
    async (id: number) => {
      await requeueFailedItem(id);
      await refreshFailed();
      await processQueue();
    },
    [processQueue, refreshFailed],
  );

  const discardItem = useCallback(
    async (id: number) => {
      await discardFailedItem(id);
      await refreshFailed();
    },
    [refreshFailed],
  );

  return {
    failedItems,
    isSyncing,
    retryItem,
    discardItem,
    syncNow: processQueue,
  };
}

export { describeSyncAction };
